import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { 
  AlertTriangle, Phone, MapPin, Plus, Trash2, Shield, 
  Clock, CheckCircle2, Users, Ambulance, Radio, Heart,
  Loader2, Navigation as NavIcon, History, User
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const RELATIONSHIPS = [
  { value: 'spouse', label: 'Spouse' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'child', label: 'Child' },
  { value: 'friend', label: 'Friend' },
  { value: 'doctor', label: 'Doctor' },
  { value: 'other', label: 'Other' }
];

const EMERGENCY_NUMBERS = [
  { name: 'Ambulance', number: '102', icon: Ambulance, color: 'bg-red-500' },
  { name: 'Emergency', number: '108', icon: Radio, color: 'bg-orange-500' },
  { name: 'Police', number: '100', icon: Shield, color: 'bg-blue-500' },
  { name: 'Women Helpline', number: '1091', icon: Heart, color: 'bg-pink-500' }
];

const SOSEmergency = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  
  // Dialog states
  const [showAddContact, setShowAddContact] = useState(false);
  const [showSOSConfirm, setShowSOSConfirm] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  
  // Form states
  const [contactForm, setContactForm] = useState({
    name: '',
    phone: '',
    relationship: '',
    is_primary: false
  });
  const [sosMessage, setSosMessage] = useState('');

  useEffect(() => {
    loadData();
    getLocation();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [contactsRes, alertsRes] = await Promise.all([
        axios.get(`${API}/emergency/contacts`),
        axios.get(`${API}/emergency/alerts`)
      ]);
      setContacts(contactsRes.data.contacts || []);
      setAlerts(alertsRes.data.alerts || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
          setLocationLoading(false);
        },
        (error) => {
          console.error('Location error:', error);
          setLocationLoading(false);
        }
      );
    } else {
      setLocationLoading(false);
    }
  };

  const handleAddContact = async () => {
    if (!contactForm.name || !contactForm.phone || !contactForm.relationship) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      await axios.post(`${API}/emergency/contacts`, contactForm);
      toast.success('Emergency contact added');
      setShowAddContact(false);
      setContactForm({ name: '', phone: '', relationship: '', is_primary: false });
      loadData();
    } catch (error) {
      toast.error('Failed to add contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/emergency/contacts/${contactId}`);
      toast.success('Contact deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const handleSendSOS = async () => {
    if (contacts.length === 0) {
      toast.error('Please add emergency contacts first');
      return;
    }
    
    setSending(true);
    try {
      const alertData = {
        alert_type: 'sos',
        latitude: location?.latitude,
        longitude: location?.longitude,
        message: sosMessage || 'Emergency! Please help!'
      };
      
      await axios.post(`${API}/emergency/alert`, alertData);
      toast.success('Emergency alert sent to all contacts!');
      setShowSOSConfirm(false);
      setSosMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to send alert');
    } finally {
      setSending(false);
    }
  };

  const handleQuickDial = (number) => {
    window.location.href = `tel:${number}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-red-600 to-orange-600">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">SOS Emergency</h1>
              <p className="text-gray-600">One-tap emergency alert system</p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowHistory(true)}>
              <History className="w-4 h-4 mr-2" />
              Alert History
            </Button>
            <Button onClick={() => setShowAddContact(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Contact
            </Button>
          </div>
        </div>

        {/* SOS Button */}
        <Card className="mb-8 bg-gradient-to-br from-red-600 to-red-700 text-white overflow-hidden">
          <CardContent className="p-8 text-center relative">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,...')] opacity-10"></div>
            <div className="relative z-10">
              <button
                onClick={() => setShowSOSConfirm(true)}
                className="w-40 h-40 rounded-full bg-white/20 backdrop-blur border-4 border-white/50 flex items-center justify-center mx-auto mb-6 hover:bg-white/30 transition-all hover:scale-105 active:scale-95 shadow-2xl"
              >
                <div className="text-center">
                  <AlertTriangle className="w-16 h-16 mx-auto mb-2" />
                  <span className="text-2xl font-bold">SOS</span>
                </div>
              </button>
              <h2 className="text-2xl font-bold mb-2">Emergency SOS</h2>
              <p className="text-red-100">Tap the button to alert all your emergency contacts</p>
              
              {/* Location Status */}
              <div className="flex items-center justify-center gap-2 mt-4">
                <MapPin className="w-4 h-4" />
                {locationLoading ? (
                  <span className="text-sm">Getting location...</span>
                ) : location ? (
                  <span className="text-sm">Location ready to share</span>
                ) : (
                  <span className="text-sm text-red-200">Location unavailable</span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Dial Numbers */}
        <h3 className="text-lg font-semibold mb-4">Quick Dial Emergency Numbers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {EMERGENCY_NUMBERS.map((num) => {
            const Icon = num.icon;
            return (
              <Card 
                key={num.number} 
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => handleQuickDial(num.number)}
              >
                <CardContent className="p-4 text-center">
                  <div className={`w-12 h-12 ${num.color} rounded-full flex items-center justify-center mx-auto mb-2`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <p className="font-medium">{num.name}</p>
                  <p className="text-2xl font-bold text-gray-800">{num.number}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Emergency Contacts */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Your Emergency Contacts</h3>
          <Badge variant="outline">{contacts.length} contacts</Badge>
        </div>
        
        {contacts.length === 0 ? (
          <Card className="p-8 text-center border-2 border-dashed">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 mb-4">No emergency contacts added yet</p>
            <Button onClick={() => setShowAddContact(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Contact
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contacts.map((contact) => (
              <Card key={contact.id} className="hover:shadow-lg transition-all">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          {contact.name}
                          {contact.is_primary && (
                            <Badge className="bg-green-100 text-green-700 text-xs">Primary</Badge>
                          )}
                        </p>
                        <p className="text-sm text-gray-500">{contact.relationship}</p>
                        <a href={`tel:${contact.phone}`} className="text-sm text-blue-600 hover:underline">
                          {contact.phone}
                        </a>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteContact(contact.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Add Contact Dialog */}
      <Dialog open={showAddContact} onOpenChange={setShowAddContact}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Emergency Contact</DialogTitle>
            <DialogDescription>This contact will be alerted during emergencies</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input 
                value={contactForm.name}
                onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                placeholder="Contact name"
              />
            </div>
            <div>
              <Label>Phone Number *</Label>
              <Input 
                value={contactForm.phone}
                onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                placeholder="+91 98765 43210"
              />
            </div>
            <div>
              <Label>Relationship *</Label>
              <Select 
                value={contactForm.relationship} 
                onValueChange={(v) => setContactForm({...contactForm, relationship: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select relationship" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIPS.map((rel) => (
                    <SelectItem key={rel.value} value={rel.value}>{rel.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="primary"
                checked={contactForm.is_primary}
                onChange={(e) => setContactForm({...contactForm, is_primary: e.target.checked})}
                className="w-4 h-4"
              />
              <Label htmlFor="primary">Set as primary contact</Label>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddContact(false)}>Cancel</Button>
            <Button onClick={handleAddContact} className="bg-red-600 hover:bg-red-700">
              Add Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* SOS Confirmation Dialog */}
      <Dialog open={showSOSConfirm} onOpenChange={setShowSOSConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirm Emergency Alert
            </DialogTitle>
            <DialogDescription>
              This will send an emergency alert to all {contacts.length} contacts
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Optional Message</Label>
              <Textarea 
                value={sosMessage}
                onChange={(e) => setSosMessage(e.target.value)}
                placeholder="Add details about your emergency (optional)"
                rows={3}
              />
            </div>
            
            {location && (
              <div className="p-3 bg-green-50 rounded-lg flex items-center gap-2">
                <MapPin className="w-4 h-4 text-green-600" />
                <span className="text-sm text-green-700">Your location will be shared</span>
              </div>
            )}
            
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                <strong>Contacts to be alerted:</strong>
              </p>
              <ul className="text-sm text-yellow-700 mt-1">
                {contacts.map(c => (
                  <li key={c.id}>• {c.name} ({c.phone})</li>
                ))}
              </ul>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSOSConfirm(false)}>Cancel</Button>
            <Button 
              onClick={handleSendSOS} 
              disabled={sending}
              className="bg-red-600 hover:bg-red-700"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send Emergency Alert
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Alert History</DialogTitle>
          </DialogHeader>
          
          {alerts.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No alerts sent yet</p>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <Card key={alert.id}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <Badge className={alert.status === 'active' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                          {alert.status}
                        </Badge>
                        <p className="text-sm text-gray-500 mt-1">
                          {new Date(alert.created_at).toLocaleString()}
                        </p>
                        <p className="text-sm mt-2">{alert.message || 'Emergency SOS'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          Notified: {alert.contacts_notified?.join(', ')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SOSEmergency;